import React, { createContext, useContext, useState, useEffect } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { useToast } from '@/components/ui/use-toast';
import api from '@/lib/api';
import { verifyToken } from '@/lib/jwtUtils';

interface User {
  id: string;
  username: string;
  email: string;
  name?: string;
}

interface AuthContextType {
  user: User | null;
  isLoading: boolean;
  login: (email: string, password: string, tenantCode?: string) => Promise<void>;
  register: (username: string, email: string, password: string) => Promise<void>;
  logout: () => void;
  isAuthenticated: boolean;
}

const AuthContext = createContext<AuthContextType | undefined>(undefined);

export const AuthProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const [user, setUser] = useState<User | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const navigate = useNavigate();
  const location = useLocation();
  const { toast } = useToast();

  useEffect(() => {
    const validateAndSetAuth = async () => {
      console.log('AuthContext: Validating authentication...');
      const token = localStorage.getItem('token');
      const currentPath = location.pathname;

      if (token) {
        try {
          // Verify token is valid and not expired
          const payload = verifyToken(token);

          if (payload) {
            console.log('AuthContext: Token is valid, fetching user profile');

            // Fetch user profile to ensure user still exists and is active
            const response = await api.get('/auth/me');
            const userData = response.data;

            setUser({
              id: userData.id || payload.sub || payload.userId,
              username: userData.username || userData.email,
              email: userData.email,
              name: userData.name
            });

            // If we're on the login page, redirect to dashboard
            if (currentPath === '/login') {
              console.log('AuthContext: Already logged in, redirecting to dashboard');
              setTimeout(() => {
                navigate('/', { replace: true });
              }, 100);
            }

            setIsLoading(false);
            return;
          } else {
            console.error('AuthContext: Token is expired or invalid');

            // Clear the invalid token
            localStorage.removeItem('token');
            setUser(null);

            // If we're not on a public page, redirect to login
            const publicRoutes = ['/login', '/register', '/verify-email', '/setup', '/forgot-password', '/reset-password', '/password-reset'];
            if (!publicRoutes.includes(currentPath)) {
              console.log('AuthContext: Token invalid, redirecting to login');
              navigate('/login', { replace: true });
            }

            setIsLoading(false);
            return;
          }
        } catch (error) {
          console.error('AuthContext: Token validation error:', error);
          localStorage.removeItem('token');
          setUser(null);
        }
      } else {
        console.log('AuthContext: No token found');
      }

      setIsLoading(false);
    };

    validateAndSetAuth();
  }, [location.pathname, navigate]);



  const login = async (email: string, password: string, tenantCode?: string) => {
    try {
      setIsLoading(true);
      // Prepare login request with tenant code if provided
      const loginRequest: any = { email, password };
      if (tenantCode) {
        loginRequest.tenantCode = tenantCode.trim().toUpperCase();
      }

      // Use the API service for login
      const response = await api.post('/auth/login', loginRequest);
      const loginData = response.data;

      // Extract token and user data from LoginResponse
      const token = loginData.token;
      const user = {
        id: loginData.userId,
        username: loginData.username || loginData.email,
        email: loginData.email,
        name: loginData.name
      };

      localStorage.setItem('token', token);
      setUser(user);
      toast({
        title: 'Login Successful',
        description: `Welcome back, ${user.name || user.email}!`,
      });
      navigate('/');
    } catch (error) {
      console.error('Login error:', error);

      // Check if it's a network/service unavailable error
      if ((error as any).response?.status === 401 || (error as any).response?.status === 400) {
        toast({
          title: 'Login Failed',
          description: 'Invalid email or password. Please try again.',
          variant: 'destructive',
        });
      } else if (!(error as any).response) {
        toast({
          title: 'Service Unavailable',
          description: 'Authentication service is currently unavailable. Please try again later.',
          variant: 'destructive',
        });
      } else {
        toast({
          title: 'Login Failed',
          description: 'An unexpected error occurred. Please try again.',
          variant: 'destructive',
        });
      }
      throw error;
    } finally {
      setIsLoading(false);
    }
  };

  const register = async (username: string, email: string, password: string) => {
    try {
      setIsLoading(true);
      const response = await api.post('/auth/register', { username, email, password });
      const { token, user } = response.data;
      localStorage.setItem('token', token);
      setUser(user);
      toast({
        title: 'Registration Successful',
        description: 'Your account has been created successfully!',
      });
      navigate('/');
    } catch (error) {
      console.error('Registration error:', error);
      toast({
        title: 'Registration Failed',
        description: 'Could not create your account. Please try again.',
        variant: 'destructive',
      });
      throw error;
    } finally {
      setIsLoading(false);
    }
  };

  const logout = () => {
    localStorage.removeItem('token');
    setUser(null);
    toast({
      title: 'Logged Out',
      description: 'You have been successfully logged out.',
    });
    navigate('/login');
  };

  return (
    <AuthContext.Provider
      value={{
        user,
        isLoading,
        login,
        register,
        logout,
        isAuthenticated: !!user,
      }}
    >
      {children}
    </AuthContext.Provider>
  );
};

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (context === undefined) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};
